import { useEffect, useState, useCallback } from 'react';

interface ParallaxOptions {
  speed?: number;
  direction?: 'up' | 'down';
  disabled?: boolean;
}

export function useParallax({ speed = 0.5, direction = 'up', disabled = false }: ParallaxOptions = {}) {
  const [offset, setOffset] = useState(0);

  const handleScroll = useCallback(() => {
    if (disabled) return;
    
    const scrollY = window.scrollY;
    const multiplier = direction === 'up' ? -1 : 1;
    setOffset(scrollY * speed * multiplier);
  }, [speed, direction, disabled]);

  useEffect(() => {
    if (disabled) return;
    
    // Use requestAnimationFrame for smoother performance
    let ticking = false;
    
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [handleScroll, disabled]);

  return offset;
}

export function useMouseParallax(intensity: number = 0.02) {
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    // Skip on touch devices — no mouse, no cost
    if (intensity === 0 || 'ontouchstart' in window) return;

    let ticking = false;

    const handleMouseMove = (e: MouseEvent) => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const centerX = window.innerWidth / 2;
          const centerY = window.innerHeight / 2;
          
          const x = (e.clientX - centerX) * intensity;
          const y = (e.clientY - centerY) * intensity;
          
          setPosition({ x, y });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [intensity]);

  return position;
}
